import { CheckIcon, ChevronUp } from "lucide-react";
import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import AuFlexiblePhoneInput from "@/components/ui/AuFlexiblePhoneInput";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input, InputProps } from "@/components/ui/input";
import { AU_COUNTRY } from "@/components/ui/phoneInput.constants";
import { isAuOnlyPhoneInput } from "@/components/ui/phoneInput.utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/core/generalUtils";

export let myCountry: RPNInput.Country;

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn("rounded-s-none rounded-e-lg shadow-sm", className)}
      ref={ref}
      {...props}
    />
  ),
);
InputComponent.displayName = "InputComponent";

const FlagComponent = ({
  country,
  countryName,
}: RPNInput.FlagProps): React.JSX.Element => {
  const Flag = flags[country];

  return (
    <span className='flex w-6 h-4 overflow-hidden rounded-sm bg-foreground/20'>
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = "FlagComponent";

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
  allowedCountries?: RPNInput.Country[];
};

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
  allowedCountries,
}: CountrySelectProps): React.JSX.Element => {
  const handleSelect = React.useCallback(
    (country: RPNInput.Country) => {
      onChange(country);
      myCountry = country;
    },
    [onChange],
  );

  const filteredOptions = React.useMemo(
    () =>
      allowedCountries
        ? options.filter((option) => allowedCountries.includes(option.value))
        : options.filter((option) => option.value),
    [allowedCountries, options],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant={"outline"}
          className={cn(
            "flex gap-1 rounded-e-none rounded-s-lg pr-1 pl-3 shadow-sm",
          )}
          disabled={disabled || filteredOptions.length === 1}
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronUp
            className={cn(
              "h-4 w-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='p-0'>
        <Command>
          <CommandList>
            <CommandGroup>
              {filteredOptions?.map((option) => (
                <CommandItem
                  className='gap-2'
                  key={option.value}
                  onSelect={() => handleSelect(option.value)}
                >
                  <FlagComponent
                    country={option.value}
                    countryName={option.label}
                  />
                  <span className='flex-1 text-sm'>{option.label}</span>

                  {option.value && (
                    <span className='text-sm text-foreground/50'>
                      {`${RPNInput.getCountryCallingCode(option.value)}`}
                    </span>
                  )}
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      option.value === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange: (value: string) => void;
    value?: string;
    allowedCountries?: RPNInput.Country[];
    allowAllCountries?: boolean;
    enableAuFlexibleInput?: boolean;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    (
      {
        className,
        onChange,
        allowedCountries = [AU_COUNTRY],
        allowAllCountries = false,
        enableAuFlexibleInput = false,
        onFocus,
        value,
        ...props
      },
      ref,
    ) => {
      const isAuOnly = isAuOnlyPhoneInput(
        props.defaultCountry ?? AU_COUNTRY,
        allowedCountries,
        allowAllCountries,
      );
      const shouldEnableAuFlexibleInput = enableAuFlexibleInput && isAuOnly;

      const handleOnChange = React.useCallback(
        (phoneNumber: RPNInput.Value) => {
          onChange(phoneNumber ?? "");
        },
        [onChange],
      );

      const handleFocus = React.useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
          onFocus?.(event);

          if (!myCountry) {
            myCountry = props.defaultCountry ?? AU_COUNTRY;
          }
        },
        [onFocus, props.defaultCountry],
      );

      if (shouldEnableAuFlexibleInput) {
        return (
          <AuFlexiblePhoneInput
            className={className}
            disabled={props.disabled}
            onBlur={props.onBlur}
            onChange={onChange}
            onFocus={handleFocus}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            ref={ref as React.Ref<HTMLInputElement>}
            value={value}
          />
        );
      }

      return (
        <RPNInput.default
          ref={ref}
          international
          defaultCountry={AU_COUNTRY}
          smartCaret
          onFocus={handleFocus}
          limitMaxLength
          countryCallingCodeEditable={false}
          className={cn("flex", className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          countrySelectProps={{
            allowedCountries: allowAllCountries ? undefined : allowedCountries,
          }}
          inputComponent={InputComponent}
          onChange={handleOnChange}
          value={value}
          {...props}
        />
      );
    },
  );
PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
